import { StateCreator } from "zustand";
import { CreateFormData } from "@/components/create/types";
import { calculateHasPrompt } from "../utils";
import type { CreatePageStore } from "../createPageStore";

export interface FormActions {
  updateFormData: (data: Partial<CreateFormData>) => void;
  resetForm: () => void;
  setValidationError: (show: boolean) => void;
  setUploadedFrontImages: (files: File[]) => void;
  setUploadedBackImages: (files: File[]) => void;
  clearUploadedImages: () => void;
  setStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  approveFrontDesign: () => void;
  setBackDesignMode: () => void;
}

/**
 * Filters out null and undefined values from partial form data updates.
 * This prevents LLM responses from wiping existing form values when they
 * don't include certain fields in their response.
 *
 * Note: Empty strings are preserved as they may represent intentional clears.
 * Boolean false and 0 are also preserved as valid values.
 */
function filterNullishValues<T extends Record<string, unknown>>(
  data: T
): Partial<T> {
  const filtered: Partial<T> = {};
  for (const key of Object.keys(data) as Array<keyof T>) {
    const value = data[key];
    // Only include values that are not null or undefined
    if (value !== null && value !== undefined) {
      filtered[key] = value;
    }
  }
  return filtered;
}

export const createFormSlice: StateCreator<
  CreatePageStore,
  [["zustand/immer", never]],
  [],
  FormActions
> = set => ({
  // Form actions
  updateFormData: (data: Partial<CreateFormData>) =>
    set((state: CreatePageStore) => {
      // Filter out null/undefined values to prevent wiping existing data
      // when LLM responses don't include certain fields
      const filteredData = filterNullishValues(data) as Partial<CreateFormData>;
      if (
        Object.prototype.hasOwnProperty.call(filteredData, "color") &&
        !Object.prototype.hasOwnProperty.call(filteredData, "colorHex")
      ) {
        filteredData.colorHex = "";
      }
      Object.assign(state.form.data, filteredData);
      state.form.hasPrompt = calculateHasPrompt(state.form.data);
      state.form.showValidationError = false;
    }),

  resetForm: () =>
    set((state: CreatePageStore) => {
      state.form.data = {
        prompt: "",
        color: "",
        colorHex: "",
        length: "",
        sleeve: "",
        sleeveAddons: [],
        neckline: "",
        waistline: "",
        silhouette: "",
        fabric: "",
        generateBack: false,
        frontApproved: false,
        backApproved: false,
        backPrompt: "",
        uploadedFrontImages: [],
        uploadedBackImages: [],
        isUploadPreset: false,
        isFrontUploadPreset: false,
        isBackUploadPreset: false,
        chatHistory: [],
        presets: [],
      };
      state.form.hasPrompt = false;
      state.form.showValidationError = false;
    }),

  setValidationError: (show: boolean) =>
    set((state: CreatePageStore) => {
      state.form.showValidationError = show;
    }),

  setUploadedFrontImages: (files: File[]) =>
    set((state: CreatePageStore) => {
      state.form.data.uploadedFrontImages = files;
      state.form.hasPrompt = calculateHasPrompt(state.form.data);
    }),

  setUploadedBackImages: (files: File[]) =>
    set((state: CreatePageStore) => {
      state.form.data.uploadedBackImages = files;
      state.form.hasPrompt = calculateHasPrompt(state.form.data);
    }),

  clearUploadedImages: () =>
    set((state: CreatePageStore) => {
      state.form.data.uploadedFrontImages = [];
      state.form.data.uploadedBackImages = [];
      state.form.hasPrompt = calculateHasPrompt(state.form.data);
    }),

  // Navigation actions
  setStep: (step: number) =>
    set((state: CreatePageStore) => {
      state.form.currentStep = step;
    }),

  nextStep: () =>
    set((state: CreatePageStore) => {
      state.form.currentStep = Math.min(
        state.form.currentStep + 1,
        state.form.totalSteps
      );
    }),

  prevStep: () =>
    set((state: CreatePageStore) => {
      state.form.currentStep = Math.max(state.form.currentStep - 1, 1);
    }),

  approveFrontDesign: () =>
    set((state: CreatePageStore) => {
      state.form.frontDesignLocked = true;
      state.form.currentStep = 3;
    }),

  setBackDesignMode: () =>
    set((state: CreatePageStore) => {
      state.form.currentStep = 3;
    }),
});
